import {
  colors,
  easingTokens,
  fontFamily,
  r4,
  shadowM,
  transitionTokens,
} from '@ardoq/design-tokens';
import { AqCloseRequestedEvent, AqQueryChangedEvent } from './events';

const DEFAULT_FIND_LABEL = 'Find';
const DEFAULT_CLOSE_LABEL = 'Close';

const TEMPLATE = `
  <style>
    :host {
      display: inline-flex;
      font-family: ${fontFamily.regular};
      font-size: 14px;
      color: ${colors.textDefault};
      opacity: 1;
      visibility: visible;
      transition:
        opacity ${transitionTokens.medium} ${easingTokens.cubicOut},
        visibility 0s;
    }
    :host([hidden]) {
      display: inline-flex;
      opacity: 0;
      visibility: hidden;
      transition:
        opacity ${transitionTokens.medium} ${easingTokens.cubicOut},
        visibility 0s linear ${transitionTokens.medium};
    }
    @media (prefers-reduced-motion: reduce) {
      :host,
      :host([hidden]) {
        transition: none;
      }
    }
    [role='search'] {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px;
      background: ${colors.surfaceDefault};
      border: 1px solid ${colors.borderSubtle};
      border-radius: ${r4};
      box-shadow: ${shadowM};
    }
    input, button {
      font: inherit;
      color: inherit;
    }
    input {
      min-width: 0;
      width: 16em;
      padding: 4px 8px;
      border: 1px solid ${colors.borderAction};
      border-radius: ${r4};
      background: ${colors.bgDefault};
    }
    input:hover {
      border-color: ${colors.borderActionHover};
    }
    input:focus-visible, button:focus-visible {
      outline: 2px solid ${colors.borderFocus};
      outline-offset: 1px;
    }
    button {
      cursor: pointer;
      padding: 4px 12px;
      border: none;
      border-radius: ${r4};
    }
    #close {
      padding: 4px 8px;
      background: transparent;
      color: ${colors.iconAction};
    }
    #close:hover {
      background: ${colors.surfaceActionGhostHover};
      color: ${colors.iconActionHover};
    }
  </style>
  <div role="search">
    <input id="query" type="text" enterkeyhint="search" autocomplete="off" />
    <button id="close" type="button">&times;</button>
  </div>`;

const queryElement = <T extends Element>(
  root: ShadowRoot,
  selector: string
) => {
  const element = root.querySelector<T & Element>(selector);
  if (!element) {
    throw new Error(`aq-find template is missing ${selector}`);
  }
  return element;
};

/**
 * A find field: a text field with a close button.
 *
 * Fires `QueryChanged` whenever the user edits the text, and `CloseRequested`
 * when the close button or Escape is pressed. Both carry the current text as
 * `detail.query`; neither changes the field. Setting `value` does not fire.
 *
 * Attributes: `value`, `placeholder`, `find-label` (the placeholder and
 * accessible name when there is no `placeholder`) and `close-label`. The
 * labels default to English, so an empty field shows "Find".
 *
 * The `hidden` attribute fades the field out, and removing it fades the field
 * back in. While hidden it cannot be focused or clicked.
 */
export class AqFindElement extends HTMLElement {
  public static readonly observedAttributes = [
    'value',
    'placeholder',
    'find-label',
    'close-label',
  ];

  private readonly input: HTMLInputElement;
  private readonly closeButton: HTMLButtonElement;

  constructor() {
    super();

    const shadow = this.attachShadow({ mode: 'open', delegatesFocus: true });
    shadow.innerHTML = TEMPLATE;

    this.input = queryElement<HTMLInputElement>(shadow, '#query');
    this.closeButton = queryElement<HTMLButtonElement>(shadow, '#close');

    this.input.addEventListener('input', this.announceQuery);
    this.closeButton.addEventListener('click', this.requestClose);
    this.input.addEventListener('keydown', this.onKeyDown);

    this.applyLabels();
  }

  /** Moves the focus into the text field and selects its text. */
  public select(): void {
    this.input.focus();
    this.input.select();
  }

  public get value(): string {
    return this.input.value;
  }

  public set value(value: string) {
    this.input.value = value;
  }

  public attributeChangedCallback(
    name: string,
    _oldValue: string | null,
    newValue: string | null
  ): void {
    if (name === 'value') {
      this.input.value = newValue ?? '';
      return;
    }

    this.applyLabels();
  }

  private applyLabels(): void {
    const findLabel = this.getAttribute('find-label') ?? DEFAULT_FIND_LABEL;
    const closeLabel = this.getAttribute('close-label') ?? DEFAULT_CLOSE_LABEL;
    const placeholder = this.getAttribute('placeholder') ?? findLabel;

    this.closeButton.setAttribute('aria-label', closeLabel);
    this.closeButton.title = closeLabel;
    this.input.placeholder = placeholder;
    this.input.setAttribute('aria-label', placeholder);
  }

  private readonly announceQuery = (): void => {
    this.dispatchEvent(new AqQueryChangedEvent({ query: this.input.value }));
  };

  private readonly requestClose = (): void => {
    this.dispatchEvent(new AqCloseRequestedEvent({ query: this.input.value }));
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.isComposing) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.requestClose();
    }
  };
}

if (!window.customElements.get('aq-find')) {
  window.customElements.define('aq-find', AqFindElement);
}

declare module 'react/jsx-runtime' {
  namespace JSX {
    interface IntrinsicElements {
      'aq-find': React.DetailedHTMLProps<
        React.HTMLAttributes<AqFindElement>,
        AqFindElement
      > & {
        value?: string;
        placeholder?: string;
        'find-label'?: string;
        'close-label'?: string;
        onQueryChanged?: (event: AqQueryChangedEvent) => void;
        onCloseRequested?: (event: AqCloseRequestedEvent) => void;
      };
    }
  }
}
